/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme';
import { authService } from '../services/authService';
import { 
  Store, 
  Save, 
  Image as ImageIcon, 
  MapPin, 
  Phone, 
  FileText,
  AlertCircle,
  CheckCircle2,
  ArrowLeft
} from 'lucide-react';
import { ROUTES } from '../routes';

const StorefrontSettings: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    storeName: '',
    storeDescription: '',
    storeLogoUrl: '',
    storeLocation: '',
    storePhone: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          navigate(ROUTES.LOGIN);
          return;
        }
        const user = JSON.parse(userStr);
        
        // Fetch fresh data from API to be sure
        const response = await authService.getUserDetails(user.id);
        
        if (response.status === 200) {
          const u = response.data.data;
          setFormData({
            storeName: u.storeName || '',
            storeDescription: u.storeDescription || '',
            storeLogoUrl: u.storeLogoUrl || '',
            storeLocation: u.storeLocation || '',
            storePhone: u.storePhone || ''
          });
        }
      } catch (err) {
        console.error("Failed to fetch profile", err);
        setError('Failed to load storefront settings.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await authService.updateProfile(formData);

      if (response.status === 200) {
        setSuccess('Storefront updated successfully!');
        // Update local storage user data
        localStorage.setItem('user', JSON.stringify(response.data.data));
        // Remove success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error?.description || 'Failed to update storefront.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.description || 'An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.colors.primary }}></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate(ROUTES.DASHBOARD)}
          className="p-2 rounded-xl border hover:bg-gray-50 transition-all flex items-center gap-2 font-bold text-sm"
          style={{ borderColor: theme.colors.border, color: theme.colors.mutedText }}
        >
          <ArrowLeft size={18} />
          Back to Dashboard
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>Storefront Settings</h1>
          <p style={{ color: theme.colors.mutedText }}>Customize how your public store appears to potential buyers.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Settings Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="p-8 rounded-3xl border shadow-sm space-y-6" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            
            {error && (
              <div className="p-4 rounded-xl border text-sm font-bold flex items-center gap-2" 
                   style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 rounded-xl border text-sm font-bold flex items-center gap-2" 
                   style={{ backgroundColor: '#10b98110', borderColor: '#10b981', color: '#10b981' }}>
                <CheckCircle2 size={18} />
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                  <Store size={14} /> Store Name
                </label>
                <input
                  type="text"
                  name="storeName"
                  value={formData.storeName}
                  onChange={handleChange}
                  placeholder="e.g. Vintage Treasures"
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                  <FileText size={14} /> Description
                </label>
                <textarea
                  name="storeDescription"
                  value={formData.storeDescription}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell buyers about your story, what you sell, and your values..."
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                  <ImageIcon size={14} /> Logo URL
                </label>
                <input
                  type="text"
                  name="storeLogoUrl"
                  value={formData.storeLogoUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/logo.png"
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                    <MapPin size={14} /> Location
                  </label>
                  <input
                    type="text"
                    name="storeLocation"
                    value={formData.storeLocation}
                    onChange={handleChange}
                    placeholder="City, State"
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                    <Phone size={14} /> Contact Phone
                  </label>
                  <input
                    type="text"
                    name="storePhone"
                    value={formData.storePhone}
                    onChange={handleChange}
                    placeholder="555-0123"
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full md:w-auto px-10 py-4 rounded-2xl font-black text-white transition-all hover:opacity-90 shadow-xl flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: theme.colors.primary }}
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save size={20} />
                  )}
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Preview Sidebar */}
        <div className="space-y-6">
          <div className="p-6 rounded-3xl border shadow-sm space-y-6" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <h3 className="font-bold border-b pb-4" style={{ color: theme.colors.text, borderColor: theme.colors.border }}>
              Storefront Preview
            </h3>
            
            <div className="text-center space-y-4">
              <div className="w-24 h-24 rounded-3xl mx-auto overflow-hidden border flex items-center justify-center bg-gray-50" 
                   style={{ borderColor: theme.colors.border }}>
                {formData.storeLogoUrl ? (
                  <img src={formData.storeLogoUrl} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <Store size={40} className="opacity-20" />
                )}
              </div>
              
              <div>
                <h4 className="text-xl font-black truncate" style={{ color: theme.colors.text }}>
                  {formData.storeName || 'Untitled Store'}
                </h4>
                <p className="text-sm mt-1 flex items-center justify-center gap-1" style={{ color: theme.colors.mutedText }}>
                  <MapPin size={14} /> {formData.storeLocation || 'No location set'}
                </p>
              </div>

              <p className="text-sm line-clamp-3 text-center px-2" style={{ color: theme.colors.mutedText }}>
                {formData.storeDescription || 'No description provided yet.'}
              </p>

              <div className="pt-2">
                <button className="w-full py-2.5 rounded-xl font-bold text-sm transition-all border"
                        style={{ borderColor: theme.colors.primary, color: theme.colors.primary }}>
                  Visit Public Storefront
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-3xl bg-blue-50 border border-blue-100 space-y-3">
            <h4 className="font-bold text-blue-900 flex items-center gap-2">
              <CheckCircle2 size={18} /> Pro Tip
            </h4>
            <p className="text-sm text-blue-800 leading-relaxed">
              Your store name and logo are the first things buyers see. Use a professional-looking logo and a descriptive name to build trust.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorefrontSettings;
